import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { boardTileService } from '../../services/request/boardTile';
import { BoardTile } from '../../services/request/boardTile.interface';
import { BoardSettings } from '../../services/request/card.interface';
import IntroAnimation from '../../components/BoardEffects/IntroAnimation';

interface Props {
	cardId: string;
	boardSettings?: BoardSettings;
	cardTitle?: string;
}

const fontSizeMap: Record<string, string> = { sm: 'text-sm', md: 'text-lg', lg: 'text-2xl' };

const withEmojiFont = (font?: string) =>
	font ? `${font}, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif` : undefined;

const TileBoardViewer: React.FC<Props> = ({ cardId, boardSettings, cardTitle }) => {
	const [tiles, setTiles] = useState<BoardTile[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [playingVideo, setPlayingVideo] = useState<string | null>(null);
	const videoRef = useRef<HTMLVideoElement>(null);

	const fetchTiles = useCallback(async () => {
		try {
			const data = await boardTileService.getTilesByCard(cardId);
			setTiles(data);
		} catch (err) {
			console.error('Failed to fetch tiles:', err);
		} finally {
			setIsLoading(false);
		}
	}, [cardId]);

	useEffect(() => {
		fetchTiles();
	}, [fetchTiles]);

	const handleVideoClick = (videoUrl: string) => {
		setPlayingVideo(videoUrl);
	};

	const closeVideoPlayer = () => {
		setPlayingVideo(null);
	};

	const showNames = boardSettings?.showContributorNames !== false;
	const bodyFont = boardSettings?.bodyFont;

	const renderTextOverlay = (content: BoardTile['content']) => {
		if (!content.textContent?.trim()) return null;
		const fSize = fontSizeMap[content.fontSize || 'md'];
		return (
			<div
				className={`p-4 rounded-lg mt-2 ${fSize}`}
				style={{
					backgroundColor: content.backgroundColor || '#f3f4f6',
					color: content.textColor || '#1f2937',
					fontFamily: withEmojiFont(content.fontFamily || bodyFont),
					textAlign: content.textAlign || 'left',
					fontWeight: content.bold ? 'bold' : 'normal',
					fontStyle: content.italic ? 'italic' : 'normal',
				}}
			>
				<p className="whitespace-pre-wrap break-words">{content.textContent}</p>
			</div>
		);
	};

	const renderTile = (tile: BoardTile) => {
		const hasTextOverlay = tile.type !== 'text' && !!tile.content.textContent?.trim();

		switch (tile.type) {
			case 'video':
				return (
					<>
						<div
							className="relative aspect-video bg-black rounded-lg overflow-hidden cursor-pointer group"
							onClick={() => tile.content.videoUrl && handleVideoClick(tile.content.videoUrl)}
						>
							{tile.content.thumbnailUrl ? (
								<img src={tile.content.thumbnailUrl} alt="" className="w-full h-full object-cover" />
							) : tile.content.videoUrl ? (
								<video src={tile.content.videoUrl} className="w-full h-full object-cover" preload="metadata" />
							) : null}
							<div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition">
								<div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition">
									<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800 ml-0.5" viewBox="0 0 24 24" fill="currentColor">
										<path d="M8 5v14l11-7z" />
									</svg>
								</div>
							</div>
							{tile.content.durationSeconds != null && (
								<span className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 text-white text-[10px] rounded font-medium">
									{Math.floor(tile.content.durationSeconds / 60)}:{(tile.content.durationSeconds % 60).toString().padStart(2, '0')}
								</span>
							)}
						</div>
						{hasTextOverlay && renderTextOverlay(tile.content)}
					</>
				);
			case 'text':
				return (
					<div
						className={`p-5 rounded-lg ${fontSizeMap[tile.content.fontSize || 'md']}`}
						style={{
							backgroundColor: tile.content.backgroundColor || '#f3f4f6',
							color: tile.content.textColor || '#1f2937',
							fontFamily: withEmojiFont(tile.content.fontFamily || bodyFont),
							textAlign: tile.content.textAlign || 'left',
							fontWeight: tile.content.bold ? 'bold' : 'normal',
							fontStyle: tile.content.italic ? 'italic' : 'normal',
						}}
					>
						<p className="whitespace-pre-wrap break-words">{tile.content.textContent}</p>
					</div>
				);
			case 'image':
				return (
					<div className="rounded-lg overflow-hidden">
						{tile.content.imageUrl && (
							<img src={tile.content.imageUrl} alt={tile.content.caption || ''} className="w-full object-cover" />
						)}
						{tile.content.caption && (
							<p className="text-sm text-gray-500 mt-2">{tile.content.caption}</p>
						)}
						{hasTextOverlay && renderTextOverlay(tile.content)}
					</div>
				);
			case 'gif':
				return (
					<div className="rounded-lg overflow-hidden">
						{tile.content.gifUrl && (
							<img src={tile.content.gifUrl} alt={tile.content.gifCaption || 'GIF'} className="w-full object-cover" />
						)}
						{tile.content.gifCaption && (
							<p className="text-sm text-gray-500 mt-2">{tile.content.gifCaption}</p>
						)}
						{hasTextOverlay && renderTextOverlay(tile.content)}
					</div>
				);
			default:
				return null;
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo"></div>
			</div>
		);
	}

	if (tiles.length === 0) {
		return (
			<div className="text-center py-20">
				<p className="text-gray-400">No content on this board yet.</p>
			</div>
		);
	}

	return (
		<>
			{/* Intro animation */}
			{boardSettings?.introAnimation && cardTitle && (
				<IntroAnimation
					title={cardTitle}
					cardId={cardId}
					titleFont={boardSettings.titleFont}
					headerColor={boardSettings.headerColor}
				/>
			)}

			<div
				className="min-h-screen"
				style={{
					backgroundColor: boardSettings?.backgroundColor || undefined,
					backgroundImage: boardSettings?.backgroundUrl ? `url(${boardSettings.backgroundUrl})` : undefined,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				{/* Header bar */}
				{cardTitle && (
					<div
						className="px-6 py-5 text-white"
						style={{ backgroundColor: boardSettings?.headerColor || '#4F46E5' }}
					>
						<h1
							className="text-3xl font-bold text-center"
							style={{ fontFamily: boardSettings?.titleFont || 'Inter' }}
						>
							{cardTitle}
						</h1>
					</div>
				)}

				{/* Masonry grid */}
				<div className="w-full max-w-4xl mx-auto px-4 py-8">
					<div style={{ columnCount: window.innerWidth >= 768 ? 3 : window.innerWidth >= 480 ? 2 : 1, columnGap: '16px' }}>
						{tiles.map((tile) => (
							<div key={tile._id} className="break-inside-avoid mb-4">
								<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
									<div className="p-3">
										{renderTile(tile)}
									</div>
									{showNames && tile.contributorName && (() => {
									const avatarColors = ['bg-indigo', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500', 'bg-red-500', 'bg-cyan-500', 'bg-orange-500'];
									let hash = 0;
									for (let c = 0; c < tile.contributorName.length; c++) hash = tile.contributorName.charCodeAt(c) + ((hash << 5) - hash);
									const colorClass = avatarColors[Math.abs(hash) % avatarColors.length];
									return (
										<div className="px-3 pb-3 flex items-center gap-1.5">
											<div className={`w-6 h-6 rounded-full ${colorClass} flex items-center justify-center shadow-sm`}>
												<span className="text-[11px] font-bold text-white">{tile.contributorName.charAt(0).toUpperCase()}</span>
											</div>
											<span className="text-xs font-medium text-gray-600">{tile.contributorName}</span>
										</div>
									);
								})()}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Video lightbox */}
			{playingVideo && (
				<div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center" onClick={closeVideoPlayer}>
					<div className="relative max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
						<button
							onClick={closeVideoPlayer}
							className="absolute -top-10 right-0 text-white/80 hover:text-white transition"
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
								<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
						<video
							ref={videoRef}
							src={playingVideo}
							controls
							autoPlay
							className="w-full rounded-xl"
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default memo(TileBoardViewer);
